/**
 * The last few sessions of one exercise, for the Progress screen.
 *
 * The charts above it answer "am I getting stronger"; this answers the question
 * you actually have standing at the rack — what did I do the last few times, set
 * by set. So it is sessions, not days and not a window of N sets: five sets last
 * Tuesday are one entry with five sets in it.
 *
 * Counting goes through lib/rounds like everywhere else, so a left and a right
 * are one set. Warm-ups are kept out of the set count and the per-set line —
 * they describe getting ready — but counted separately, because "3 sets" when
 * you also did two ramp-up sets is true and a little misleading.
 *
 * Dexie-free (type-only imports), so it runs under node:test.
 */

import { countRounds, groupRounds, type Round, type Sided } from './rounds.ts'

export type SessionSet = Sided & {
  performed_at: string
  order_index: number
}

export type RecentSession<T extends SessionSet> = {
  sessionId: string
  /** When the session's first set of this exercise was logged. */
  performedAt: string
  /** Working sets, as rounds, in the order they were logged. */
  rounds: Round<T>[]
  setCount: number
  warmupCount: number
}

/**
 * Group one exercise's sets into its most recent sessions, newest first.
 *
 * Input order doesn't matter. A session with nothing but warm-ups is still
 * listed — it happened — and shows zero working sets.
 */
export function recentSessions<T extends SessionSet>(
  sets: readonly T[],
  limit = 5,
): RecentSession<T>[] {
  const bySession = new Map<string, T[]>()
  for (const s of sets) {
    const rows = bySession.get(s.session_id)
    if (rows) rows.push(s)
    else bySession.set(s.session_id, [s])
  }

  const sessions = [...bySession.entries()].map(([sessionId, rows]) => {
    // Logged order within the session: set_index is what the lifter saw
    // ("Set 2"), order_index breaks a tie between a left and a right.
    rows.sort((a, b) => a.set_index - b.set_index || a.order_index - b.order_index)
    const performedAt = rows.reduce(
      (min, s) => (s.performed_at < min ? s.performed_at : min),
      rows[0].performed_at,
    )
    const latest = rows.reduce(
      (max, s) => (s.performed_at > max ? s.performed_at : max),
      rows[0].performed_at,
    )
    const working = rows.filter((s) => !s.is_warmup)
    return {
      latest,
      session: {
        sessionId,
        performedAt,
        rounds: groupRounds(working),
        setCount: countRounds(working),
        warmupCount: countRounds(rows.filter((s) => s.is_warmup)),
      },
    }
  })

  return sessions
    .sort((a, b) => (a.latest < b.latest ? 1 : a.latest > b.latest ? -1 : 0))
    .slice(0, limit)
    .map((s) => s.session)
}
